// GlassLoginForm.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { 
  Lock, 
  Mail, 
  Eye, 
  EyeOff, 
  LogIn, 
  Users,
  Shield,
  ArrowLeft,
  TrendingUp,
  BarChart3,
  LineChart,
  DollarSign,
  Sparkles,
  Fingerprint
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';
import axios from 'axios';

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    login: '',  // Changed from 'email' to 'login' - single field for both email and TIN
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const [inputType, setInputType] = useState(''); // Track if input is email or TIN
  const navigate = useNavigate();

  // Configure axios instance
  const api = axios.create({
    baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8000/api',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    }
  });

  // Add token to requests if exists
  api.interceptors.request.use(
    config => {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    error => {
      return Promise.reject(error);
    }
  );

 

  const handleChange = (e) => {
    const value = e.target.value;
    
    // Detect input type for dynamic UI feedback
    if (value.includes('@')) {
      setInputType('email');
    } else if (/^\d+$/.test(value)) {
      setInputType('tin');
    } else {
      setInputType('');
    }
    
    setFormData({
      ...formData,
      [e.target.name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Detect if input is email or TIN
      const loginValue = formData.login;
      const isEmail = loginValue.includes('@');
      
      // Prepare request based on input type
      let requestData;
      if (isEmail) {
        requestData = {
          email: loginValue,  // Send as email
          password: formData.password
        };
      } else {
        requestData = {
          tin: loginValue,    // Send as TIN
          password: formData.password
        };
      }

      const response = await api.post('/login', requestData);

      if (response.status === 200 || response.status === 201) {
        if (response.data.token) {
          localStorage.setItem('token', response.data.token);
     navigate('/admin/dashboard');
        }

        await Swal.fire({
          icon: 'success',
          title: 'Login Successful!',
          text: response.data.message || 'Welcome back!',
          confirmButtonColor: '#4f46e5',
          timer: 2000,
          timerProgressBar: true,
        });

        

      } else {
        await Swal.fire({
          icon: 'error',        
          title: 'Login Failed',
          text: response.data.message || 'Invalid credentials, please try again.',
          confirmButtonColor: '#4f46e5',
        });
      }

    } catch (error) {
      console.error('Login error:', error);
      await Swal.fire({
        icon: 'error',  
        title: 'Login Failed',
        text: error.response?.data?.message || 'An error occurred during login. Please try again.',
        confirmButtonColor: '#4f46e5',
      });
    } finally {
      setLoading(false);
    }
  };

 
     

  // Get dynamic placeholder text
  const getPlaceholder = () => {
    if (inputType === 'email') {
      return "Enter your email address";
    } else if (inputType === 'tin') {
      return "Enter your TIN number";
    } else {
      return "Email or TIN (e.g., trader@example.com or 123456789)";
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden bg-gradient-to-br from-slate-900 via-indigo-950 to-purple-950">
      {/* Animated Background Elements with Blue-Violet Theme */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Gradient Orbs - Blue and Violet */}
        <div className="absolute top-0 -left-4 w-96 h-96 bg-blue-500/20 rounded-full mix-blend-soft-light filter blur-3xl animate-pulse-slow"></div>
        <div className="absolute top-1/3 -right-4 w-96 h-96 bg-indigo-500/20 rounded-full mix-blend-soft-light filter blur-3xl animate-pulse-slow animation-delay-1000"></div>
        <div className="absolute bottom-0 left-1/3 w-96 h-96 bg-purple-500/20 rounded-full mix-blend-soft-light filter blur-3xl animate-pulse-slow animation-delay-2000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-violet-500/10 rounded-full mix-blend-soft-light filter blur-3xl animate-spin-slow"></div>
        
        {/* Animated Chart Lines Background */}
        <div className="absolute inset-0 opacity-10">
          <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="chart-grid" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="rgba(99, 102, 241, 0.3)" strokeWidth="0.5"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#chart-grid)" />
          </svg>
        </div>

        {/* Floating Candlestick Pattern */}
        <div className="absolute bottom-10 right-10 opacity-5">
          <div className="flex space-x-1">
            {[30, 45, 25, 50, 35, 55, 40].map((height, i) => (
              <div key={i} className="flex flex-col items-center w-3">
                <div className={`w-1 bg-blue-400 rounded-t`} style={{ height: `${height}px` }}></div>
                <div className={`w-3 h-${Math.floor(height/5)} bg-indigo-400 rounded`} style={{ height: `${height/3}px` }}></div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="relative w-full max-w-lg px-4">
        {/* Main Card with Premium Glass Effect */}
        <div className="relative backdrop-blur-xl bg-white/5 rounded-3xl shadow-[0_20px_50px_rgba(0,0,0,0.5)] border border-white/10 overflow-hidden">
          {/* Animated Gradient Border */}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-blue-500/20 to-transparent -translate-x-full animate-shimmer"></div>
          
          {/* Top Gradient Bar - Blue to Violet */}
          <div className="h-1.5 bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500"></div>
          
          <div className="relative p-8 md:p-10">
            {/* Header with Trade Logo and Design */}
            <div className="text-center mb-10">
              {/* Animated Trade Logo */}
              <div className="relative inline-flex mb-8">
                {/* Outer Glow */}
                <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-600 rounded-3xl blur-2xl opacity-40 animate-pulse"></div>
                
                {/* Main Logo Container */}
                <div className="relative bg-gradient-to-br from-blue-500 via-indigo-500 to-purple-600 p-5 rounded-3xl shadow-2xl">
                  <div className="relative">
                    {/* Trading Chart Icon */}
                    <div className="flex items-center justify-center space-x-1 mb-1">
                      <BarChart3 className="h-8 w-8 text-white" />
                      <TrendingUp className="h-8 w-8 text-white" />
                    </div>
                    {/* Dollar Sign Overlay */}
                    <div className="absolute -top-2 -right-2 bg-white rounded-full p-1.5 shadow-lg">
                      <DollarSign className="h-4 w-4 text-indigo-600" />
                    </div>
                  </div>
                </div>
              </div>
              
              <p className="text-4xl md:text-5xl font-bold mb-3">
                <span className="bg-gradient-to-r from-blue-300 via-indigo-200 to-purple-300 bg-clip-text text-transparent">
                  ETIMS
                </span>
              </p>
           
              {/* Decorative Line with Blue-Violet Gradient */}
              <div className="flex items-center justify-center space-x-2 mt-4">
                <div className="w-12 h-0.5 bg-gradient-to-r from-transparent via-blue-400 to-transparent"></div>
                <div>
                  <p className='text-indigo-700 text-sm'>ETHIOPIAN TRADE INSPECTION and Management System (ETIMS)</p>
                </div>
                <div className="w-12 h-0.5 bg-gradient-to-r from-transparent via-purple-400 to-transparent"></div>
              </div>
            </div>
           

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Login Field - Accepts Email OR TIN */}
              <div className="group">
                <div className="flex items-center space-x-2 mb-2">
                  <Mail className="h-5 w-5 text-blue-400 group-focus-within:text-purple-400 transition-colors" />
                  <label className="text-sm font-medium text-indigo-700 group-focus-within:text-purple-200 transition-colors">
                    Email Address or TIN Number
                  </label>
                </div>
                <div className="relative">
                  <input
                    type="text"
                    name="login"
                    value={formData.login}
                    onChange={handleChange}
                    onFocus={() => setFocusedField('login')}
                    onBlur={() => setFocusedField(null)}
                    className="w-full px-6 py-4 border  bg-white text-black  rounded-2xl  placeholder-black focus:outline-none focus:text-black focus:border-indigo-400 focus:ring-4 focus:ring-indigo-500/20 transition-all duration-300"
                    placeholder={getPlaceholder()}
                    required
                    disabled={loading}
                  />
                  {/* Show icon based on input type */}
                  {inputType === 'email' && (
                    <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
                      <Mail className="h-5 w-5 text-green-400" />
                    </div>
                  )}
                  {inputType === 'tin' && (
                    <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
                      <Fingerprint className="h-5 w-5 text-purple-400" />
                    </div>
                  )}
                  {/* Animated Gradient Border on Focus */}
                  {focusedField === 'login' && (
                    <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-blue-500 to-purple-500 -z-10 blur opacity-40 animate-pulse"></div>
                  )}
                </div>
                {/* Helper text based on input type */}
                <p className="text-xs text-blue-300/70 mt-2">
                  {inputType === 'email' && "✓ Logging in with email address"}
                  {inputType === 'tin' && "✓ Logging in with TIN number"}
                  {!inputType && formData.login && "⚠️ Enter a valid email address or numeric TIN"}
                  {!formData.login && "Enter your registered email address or TIN number"}
                </p>
              </div>

              {/* Password Field with Professional Design */}
              <div className="group">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <Lock className="h-5 w-5 text-blue-400 group-focus-within:text-purple-400 transition-colors" />
                    <label className="text-sm font-medium text-blue-500 group-focus-within:text-purple-200 transition-colors">
                      Password
                    </label>
                  </div>
                  <a 
                    href="/forgotpassword" 
                    className="text-sm text-blue-500 hover:text-purple-300 transition-colors"
                  >
                    Forgot Password?
                  </a>
                </div>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    onFocus={() => setFocusedField('password')}
                    onBlur={() => setFocusedField(null)}
                                      className="w-full px-6 py-4 border  bg-white text-black  rounded-2xl text-black placeholder-black focus:outline-none focus:border-indigo-400 focus:ring-4 focus:ring-indigo-500/20 transition-all duration-300"
                    placeholder="••••••••"
                    required
                    disabled={loading}
                  />
                  
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-black--400 hover:text-white transition-colors"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                  
                  {/* Animated Gradient Border on Focus */}
                  {focusedField === 'password' && (
                    <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-blue-500 to-purple-500 -z-10 blur opacity-40 animate-pulse"></div>
                  )}
                </div>
              </div>

              {/* Remember Me & Security Badge */}
              <div className="flex items-center justify-between">
                <label className="flex items-center space-x-2 cursor-pointer group">
                  <input 
                    type="checkbox" 
                    className="w-4 h-4 rounded border-white/30 bg-white/10 text-indigo-600 focus:ring-indigo-500 focus:ring-offset-0 transition-colors" 
                  />
                  <span className="text-sm text-blue-500 group-hover:text-blue-100 transition-colors">Remember me</span>
                </label>
                <div className="flex items-center space-x-1 text-xs text-blue-300">
                  <Fingerprint className="h-4 w-4 text-purple-400" />
                  <span>2FA Available</span>
                </div>
              </div>

              {/* Submit Button with Blue-Violet Gradient */}
              <button
                type="submit"
                disabled={loading}
                className="w-full group relative overflow-hidden bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white font-semibold py-4 px-6 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 disabled:opacity-70 disabled:cursor-not-allowed transform hover:scale-[1.02] active:scale-[0.98]"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-blue-700 via-indigo-700 to-purple-700 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="relative flex items-center justify-center space-x-3">
                  {loading ? (
                    <>
                      <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span className="text-lg">Accessing Trade Desk...</span>
                    </>
                  ) : (
                    <>
                      <LogIn className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                      <span className="text-lg">Login</span>
                    </>
                  )}
                </div>
              </button>
            </form>
          </div>

          {/* Footer with Professional Design */}
          <div className="px-8 py-5 bg-black/30 backdrop-blur-sm border-t border-white/10">
            <div className="flex items-center justify-center space-x-6 text-xs text-blue-200/60">
              <span>•</span>
              <span>© 2024 ETIMS</span>
              <span>•</span>
              <a href="/privacy" className="hover:text-purple-300 transition-colors">Privacy</a>
            </div>
          </div>
        </div>
      </div>

      {/* Add custom animations */}
      <style jsx>{`
        @keyframes pulse-slow {
          0%, 100% { opacity: 0.2; transform: scale(1); }
          50% { opacity: 0.3; transform: scale(1.05); }
        }
        .animate-pulse-slow {
          animation: pulse-slow 4s infinite ease-in-out;
        }
        .animation-delay-1000 {
          animation-delay: 1s;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        @keyframes spin-slow {
          from { transform: translate(-50%, -50%) rotate(0deg); }
          to { transform: translate(-50%, -50%) rotate(360deg); }
        }
        .animate-spin-slow {
          animation: spin-slow 20s linear infinite;
        }
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        .animate-shimmer {
          animation: shimmer 3s infinite;
        }
      `}</style>
    </div>
  );
};

export default Login;